/**
 * @fileoverview The letterbox missions page, server-side.
 *
 * The legacy page (../container/LetterboxMissions.js) loaded every mission
 * of the plan, drew every polygon, asked PathFinder for every density and
 * reloaded the whole route after each action - unusable at 3,000 missions.
 * This one holds one page of rows and the polygons in the viewport, keeps
 * its selection as "these ids" or "everything matching this filter", and
 * sends bulk actions to the API as one call. After an action it bumps the
 * context's missionsVersion and refreshes the counts; nothing remounts.
 */
import React, { useCallback, useContext, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { useParams } from 'react-router-dom'
import Row from 'react-bootstrap/lib/Row'
import Col from 'react-bootstrap/lib/Col'
import Typography from '@material-ui/core/Typography'

import { LayoutDashboard } from 'Components/Layout/Dashboard'
import { PageTitle } from 'Components/PageTitle'
import { SectionWrapper } from 'Components/Section/SectionWrapper'
import DetailsSection from 'Components/DetailsSection/DetailsSection'
import ShiftsPanel from './ShiftsPanel'
import BreadcrumbsRenderer from 'Components/BreadcrumbsRenderer/BreadcrumbsRenderer'
import BulkActions from 'Components/BulkActions'
import BulkEditMissionModalBody from 'Components/BulkActions/blocks/BulkEditMissionModalBody'
import SetMeetingPointModal from 'Components/BulkActions/blocks/SetMeetingPointModal'
import SubmitButton from 'Components/Form/SubmitButton'
import { SnackBarContext } from 'Components/SnackBar'
import MissionBoundariesMap from 'Components/MissionBoundariesMap'
import { readGraphQLData } from 'Components/PowerBanks/hooks'
import {
  describeBulkResult,
  runBulkJob,
} from 'Components/MissionsControls/utils'
import { authorization } from 'Module/Auth/Auth'
import { apiClient } from 'Module/API/Client'
import {
  fetchMissionBulkJob,
  publishCityPlanMissions,
  setMissionsReadyToPublish,
  updateMissionNormalized,
  updateMissionsBulk,
} from 'Module/graphQL'
import { hideReduxModal, showReduxModal } from 'Reducers/UI/actions'
import {
  clearSelection,
  detailsItemsFromStats,
  emptySelection,
  isSelected,
  isSelectionEmpty,
  isoWeekKey,
  parseIsoWeekKey,
  selectAllMatching,
  selectIds,
  selectionCount,
  selectionToLabel,
  toSelectionInput,
  toggleId,
} from 'Utils/CityPlan'
import { STATUS_CAMPAIGN_ENUM_ACTIVE } from 'shared/constants'
import { getBreadcrumbs } from '../util'
import { useCityPlan } from 'Containers/Campaigns/CityPlan/CityPlanContext'
import { useMissionBoundaries } from 'Containers/Campaigns/CityPlan/hooks/useMissionBoundaries'
import MissionsTableV2 from './MissionsTableV2'
import MissionAreaEditorModal from './MissionAreaEditorModal'
import { SelectionProvider } from './SelectionContext'

const MAP_HEIGHT = 520

const LetterboxMissionsV2 = props => {
  const { campaign, city, countryCode, match, weekScoped } = props
  // The roster route's :rosterId is a child param, not in the router's match.
  const { rosterId } = useParams()
  const isoWeek = useMemo(() => {
    if (!weekScoped || !rosterId) return null
    const parsed = parseIsoWeekKey(rosterId)
    return parsed ? isoWeekKey(parsed.isoYear, parsed.isoWeek) : rosterId
  }, [weekScoped, rosterId])
  const { campaignId, cityPlanId } = match.params
  const {
    stats,
    refreshStats,
    missionsVersion,
    refreshMissions,
  } = useCityPlan()
  const runSnackBar = useContext(SnackBarContext)
  const canManage = authorization.isAdmin() || authorization.isCityManager()

  // ---- selection ----------------------------------------------------------
  const [selection, setSelection] = useState(emptySelection())
  const [page, setPage] = useState({
    missions: [],
    totalCount: 0,
    filter: {},
    orderBy: 'DATETIME_ASC',
  })
  const onToggle = useCallback(id => setSelection(sel => toggleId(sel, id)), [])
  const onSelectPage = useCallback(
    () => setSelection(sel => selectIds(sel, page.missions.map(m => m.id))),
    [page.missions],
  )
  const onSelectAllMatching = useCallback(
    () => setSelection(selectAllMatching(page.filter)),
    [page.filter],
  )
  const onClearSelection = useCallback(() => setSelection(clearSelection()), [])
  const selectedCount = selectionCount(selection, page.totalCount)
  const nothingSelected = isSelectionEmpty(selection, page.totalCount)
  const selectionInput = useMemo(() => toSelectionInput(selection), [selection])
  const selectionContext = useMemo(() => ({ selection, onToggle }), [
    selection,
    onToggle,
  ])

  // ---- map ----------------------------------------------------------------
  const [viewport, setViewport] = useState({ bbox: null, zoom: 0 })
  const [showPublished, setShowPublished] = useState(true)
  const mapFilter = useMemo(() => {
    const filter = { ...page.filter }
    delete filter.search
    if (isoWeek) filter.isoWeeks = [isoWeek]
    return filter
  }, [page.filter, isoWeek])
  const boundaries = useMissionBoundaries({
    cityPlanId,
    filter: mapFilter,
    bbox: viewport.bbox,
    version: missionsVersion,
    enabled: viewport.zoom >= 11,
  })
  const onSelectVisible = useCallback(
    ids => setSelection(sel => selectIds(sel, ids)),
    [],
  )
  const isSelectedFn = useCallback(id => isSelected(selection, id), [selection])
  const onPolygonToggle = useCallback(item => onToggle(item.id), [onToggle])

  // ---- after any change ---------------------------------------------------
  const afterChange = useCallback(() => {
    refreshStats()
    refreshMissions()
  }, [refreshStats, refreshMissions])

  // ---- bulk actions -------------------------------------------------------
  const bulk = useCallback(
    ({ title, verb, start }) =>
      runBulkJob({
        showReduxModal: props.showReduxModal,
        hideReduxModal: props.hideReduxModal,
        title,
        verb,
        total: selectedCount,
        start,
        fetchJob: async id =>
          (await readGraphQLData(await fetchMissionBulkJob(id))).missionBulkJob,
        onDone: result => {
          runSnackBar({
            type: 'success',
            msg: describeBulkResult(result, verb),
          })
          setSelection(clearSelection())
          afterChange()
        },
      }),
    [
      props.showReduxModal,
      props.hideReduxModal,
      selectedCount,
      runSnackBar,
      afterChange,
    ],
  )

  // runBulkJob replaces this dialog with progress and hides it itself on
  // success; on failure it shows an error dialog that must stay up, so
  // ReduxModal's own hide-after-confirm is switched off.
  const confirmThen = (title, body, action) =>
    props.showReduxModal({
      title,
      children: body,
      confirmLabel: 'Confirm',
      onConfirm: action,
      hideModalOnConfirm: false,
    })

  const onSetReady = ready =>
    confirmThen(
      `${
        ready ? 'Mark' : 'Unmark'
      } ${selectedCount.toLocaleString()} missions ${
        ready ? 'as ready to publish' : 'ready to publish'
      }`,
      'Only draft missions are affected.',
      () =>
        bulk({
          title: ready
            ? 'Marking ready to publish'
            : 'Unmarking ready to publish',
          verb: ready ? 'Marked' : 'Unmarked',
          start: async () =>
            (await readGraphQLData(
              await setMissionsReadyToPublish({
                cityPlanId,
                selection: selectionInput,
                ready,
              }),
            )).setMissionsReadyToPublish,
        }),
    )

  const onPublish = () => {
    if (campaign.status !== STATUS_CAMPAIGN_ENUM_ACTIVE)
      return props.showReduxModal({
        title: 'Draft campaign',
        children:
          'Missions are allowed to be published only if campaign is active.',
        confirmLabel: 'OK',
        hideCancel: true,
      })
    return confirmThen(
      `Publish ${selectedCount.toLocaleString()} missions`,
      'Draft missions in the selection become available to brand ambassadors. Missions already published, in the past or more than six weeks away are skipped and reported.',
      () =>
        bulk({
          title: 'Publishing missions',
          verb: 'Published',
          start: async () =>
            (await readGraphQLData(
              await publishCityPlanMissions({
                cityPlanId,
                selection: selectionInput,
              }),
            )).publishCityPlanMissions,
        }),
    )
  }

  // `selectionOverride` lets a row action edit one mission without waiting
  // for the selection state (the modal's children are captured on open).
  const submitBulkPatch = useCallback(
    async (patch, selectionOverride = null) => {
      const result = (await readGraphQLData(
        await updateMissionsBulk({
          cityPlanId,
          selection: selectionOverride || selectionInput,
          patch,
          labelIndexOrderBy: page.orderBy,
        }),
      )).updateMissionsBulk
      afterChange()
      return result
    },
    [cityPlanId, selectionInput, page.orderBy, afterChange],
  )

  const openEditModal = ({ missions, count, selectionOverride }) =>
    props.showReduxModal({
      title:
        count === 1 && missions[0]
          ? `Edit mission ${missions[0].label || missions[0].id}`
          : `Edit ${count.toLocaleString()} missions`,
      children: (
        <BulkEditMissionModalBody
          missionsToEdit={missions}
          selectedCount={count}
          city={city}
          onSubmitBulk={patch => submitBulkPatch(patch, selectionOverride)}
          onClose={() => setSelection(clearSelection())}
          currencySign={(city.country && city.country.currencySign) || ''}
        />
      ),
      hideButtons: true,
      hideCancel: true,
    })

  const onBulkEdit = () =>
    openEditModal({
      missions:
        selectedCount === 1
          ? page.missions.filter(m => isSelected(selection, m.id))
          : [],
      count: selectedCount,
      selectionOverride: null,
    })

  // The colour of one area, from the swatch on its label. Colours are a
  // Mission field rather than a bulk patch, so this goes through the same
  // updateMission the area editor uses.
  const onRecolourMission = async (mission, boundaryColour) => {
    const response = await updateMissionNormalized({
      missionInput: { id: Number(mission.id), boundaryColour },
    })
    const result = await response.json()
    if (result.errors && result.errors.length)
      throw new Error(result.errors[0].message)
    refreshMissions()
    runSnackBar({ type: 'success', msg: 'Colour updated.' })
  }

  // In-place rename from the table's Label cell or the map popup.
  const onRenameMission = async (mission, label) => {
    const result = await submitBulkPatch(
      { label },
      { missionIds: [mission.id] },
    )
    runSnackBar({ type: 'success', msg: describeBulkResult(result, 'Renamed') })
  }

  // The row's "Edit" link: rename / re-date one mission from the table,
  // the way the legacy roster page's inline editing did.
  const onEditRow = mission => {
    setSelection(selectIds(emptySelection(), [mission.id]))
    openEditModal({
      missions: [mission],
      count: 1,
      selectionOverride: { missionIds: [mission.id] },
    })
  }

  // ---- meeting point ------------------------------------------------------
  const [meetingPointOpen, setMeetingPointOpen] = useState(false)
  const [meetingPointSamples, setMeetingPointSamples] = useState([])
  const boundaryById = useMemo(
    () =>
      boundaries.items.reduce((acc, item) => {
        acc[String(item.id)] = item.boundary
        return acc
      }, {}),
    [boundaries.items],
  )
  const openMeetingPoint = missions => {
    const fallback = [
      { latitude: Number(city.latitude), longitude: Number(city.longitude) },
    ]
    setMeetingPointSamples(
      missions.map(mission => ({
        ...mission,
        boundary: boundaryById[String(mission.id)] || fallback,
      })),
    )
    setMeetingPointOpen(true)
  }
  const onBulkMeetingPoint = () => {
    const selectedOnPage = page.missions.filter(m =>
      isSelected(selection, m.id),
    )
    openMeetingPoint(
      selectedOnPage.length ? selectedOnPage : page.missions.slice(0, 1),
    )
  }
  // MissionTableActions calls this as onEditMeetingPoint({ mission }).
  const onSingleMeetingPoint = ({ mission }) => {
    setSelection(selectIds(emptySelection(), [mission.id]))
    openMeetingPoint([mission])
  }
  const submitMeetingPoint = async meetingPoint => {
    const result = await submitBulkPatch({ meetingPoint })
    runSnackBar({ type: 'success', msg: describeBulkResult(result, 'Updated') })
  }

  // ---- delete -------------------------------------------------------------
  const DELETE_BATCH = 25
  const onDelete = ids =>
    props.showReduxModal({
      title:
        ids.length === 1
          ? 'Delete 1 non-started mission'
          : `Delete ${ids.length.toLocaleString()} non-started missions`,
      children:
        'Only draft, available and booked missions are deleted; booked ones are unbooked first.',
      confirmLabel: 'Delete',
      onConfirm: async () => {
        let deleted = 0
        for (let start = 0; start < ids.length; start += DELETE_BATCH) {
          const batch = ids.slice(start, start + DELETE_BATCH)
          const response = await apiClient.deleteMissions({
            unbookReason: 'City manager deleted the mission',
            missions: batch.map(id => ({ id })),
          })
          if (!response.ok) {
            runSnackBar({
              type: 'error',
              msg: `Deleting stopped after ${deleted} missions: the server refused the request.`,
            })
            break
          }
          deleted += batch.length
        }
        if (deleted > 0)
          runSnackBar({ type: 'success', msg: `Deleted ${deleted} missions` })
        setSelection(clearSelection())
        afterChange()
      },
    })
  const onBulkDelete = () => {
    if (selection.mode !== 'ids')
      return props.showReduxModal({
        title: 'Select the missions to delete',
        children:
          'Deleting works on ticked missions. Use "Select this page" (repeat for more pages), or delete a whole draft plan from the city plan page.',
        confirmLabel: 'OK',
        hideCancel: true,
      })
    return onDelete(Array.from(selection.ids).map(Number))
  }

  // ---- area editor --------------------------------------------------------
  const [areaEditor, setAreaEditor] = useState({ open: false, mission: null })

  // ---- render -------------------------------------------------------------
  const breadcrumbsData = useMemo(
    () => getBreadcrumbs({ campaign, campaignId, city }),
    [campaign, campaignId, city],
  )
  const weekStats = useMemo(
    () =>
      isoWeek && stats
        ? (stats.weeks || []).find(week => week.key === isoWeek) || null
        : stats,
    [stats, isoWeek],
  )
  const detailsItems = useMemo(() => detailsItemsFromStats(weekStats), [
    weekStats,
  ])
  const weekLabel = isoWeek
    ? `Week ${isoWeek.split('-')[1]}, ${isoWeek.split('-')[0]}`
    : null
  const footer = useMemo(
    () => ({
      Select: 'Total:',
      Label: (page.totalCount || 0).toLocaleString(),
    }),
    [page.totalCount],
  )

  return (
    <LayoutDashboard>
      <BreadcrumbsRenderer
        icon={breadcrumbsData.icon}
        items={breadcrumbsData.items}
      />
      <PageTitle>
        {weekLabel
          ? `${city ? `${city.name} ` : ''}${weekLabel}`
          : city
          ? `${city.name} missions`
          : 'Missions'}
      </PageTitle>
      {isoWeek && (
        <Typography
          variant="caption"
          style={{ display: 'block', marginBottom: 8 }}
        >
          Showing this week only.{' '}
          <a href={`${match.url}?legacy=1`}>Legacy roster page</a> (CSV import,
          inline editing).
        </Typography>
      )}
      <DetailsSection items={detailsItems} />

      <SectionWrapper className="section-wrapper">
        <Row>
          <Col xs={12}>
            <Typography variant="h6" style={{ marginBottom: 8 }}>
              Shifts and standby BAs
            </Typography>
            <Typography
              variant="caption"
              style={{ display: 'block', marginBottom: 12 }}
            >
              One row per meeting point. Open a shift to set how many standby
              BAs it carries, hand a mission to a standby when its BA does not
              show, or send an unused standby home on half pay.
            </Typography>
            <ShiftsPanel
              cityPlanId={cityPlanId}
              campaignId={campaignId}
              cityId={city && city.id}
              canManage={canManage}
            />
          </Col>
        </Row>
      </SectionWrapper>

      <SelectionProvider value={selectionContext}>
        <SectionWrapper className="section-wrapper">
          <Row>
            <Col xs={12}>
              <MissionBoundariesMap
                city={city}
                items={boundaries.items}
                loading={boundaries.loading}
                totalCount={boundaries.totalCount}
                isSelected={isSelectedFn}
                onToggle={onPolygonToggle}
                onRename={canManage ? onRenameMission : null}
                onRecolour={canManage ? onRecolourMission : null}
                onEdit={canManage ? onEditRow : null}
                onEditArea={
                  canManage
                    ? mission =>
                        setAreaEditor({
                          open: true,
                          mission: { ...mission, hasBoundary: true },
                        })
                    : null
                }
                onSelectVisible={canManage ? onSelectVisible : null}
                onViewportChange={setViewport}
                height={MAP_HEIGHT}
                showPublished={showPublished}
                onShowPublishedChange={setShowPublished}
              />
              <Typography
                variant="caption"
                style={{ display: 'block', marginTop: 6 }}
              >
                The map shows the areas in view for the current filter. Click an
                area to select it; use the table below to filter, search and
                page through all missions.
              </Typography>
            </Col>
          </Row>
        </SectionWrapper>

        {canManage && (
          <SectionWrapper className="section-wrapper">
            <Row>
              <Col
                xs={12}
                style={{ display: 'flex', gap: 12, alignItems: 'center' }}
              >
                <strong>{selectionToLabel(selection, page.totalCount)}</strong>
                <SubmitButton
                  value={`Select this page (${page.missions.length})`}
                  size="small"
                  variant="outlined"
                  disabled={page.missions.length === 0}
                  onClick={onSelectPage}
                />
                <SubmitButton
                  value={`Select all ${(
                    page.totalCount || 0
                  ).toLocaleString()} matching`}
                  size="small"
                  variant="outlined"
                  disabled={!page.totalCount}
                  onClick={onSelectAllMatching}
                />
                <SubmitButton
                  value="Clear"
                  size="small"
                  variant="outlined"
                  disabled={nothingSelected}
                  onClick={onClearSelection}
                />
                <SubmitButton
                  value="Draw a new area"
                  size="small"
                  variant="outlined"
                  onClick={() => setAreaEditor({ open: true, mission: null })}
                />
                <BulkActions
                  disabled={nothingSelected}
                  onPublish={onPublish}
                  onMarkReadyToPublish={() => onSetReady(true)}
                  onUnmarkReadyToPublish={() => onSetReady(false)}
                  onEdit={onBulkEdit}
                  onSetMarker={onBulkMeetingPoint}
                  onDelete={onBulkDelete}
                />
              </Col>
            </Row>
          </SectionWrapper>
        )}

        <SectionWrapper className="section-wrapper">
          <MissionsTableV2
            cityPlanId={cityPlanId}
            campaign={campaign}
            city={city}
            weeks={(stats && stats.weeks) || []}
            isoWeek={isoWeek}
            selection={selection}
            onToggle={onToggle}
            onPage={setPage}
            onDelete={canManage ? id => onDelete([Number(id)]) : null}
            onEditMeetingPoint={canManage ? onSingleMeetingPoint : null}
            onRename={canManage ? onRenameMission : null}
            onRecolour={canManage ? onRecolourMission : null}
            onEditRow={canManage ? onEditRow : null}
            onEditArea={
              canManage
                ? mission => setAreaEditor({ open: true, mission })
                : null
            }
            refreshRequestTrigger={missionsVersion}
            editable={canManage}
            footer={footer}
          />
        </SectionWrapper>
      </SelectionProvider>

      {meetingPointOpen && (
        <SetMeetingPointModal
          isOpen
          city={city}
          countryCode={countryCode}
          missionsToEdit={meetingPointSamples}
          onClose={() => setMeetingPointOpen(false)}
          onRefetchMissions={() => {}}
          onSubmitBulk={submitMeetingPoint}
        />
      )}

      {areaEditor.open && (
        <MissionAreaEditorModal
          show
          mission={areaEditor.mission}
          cityPlanId={cityPlanId}
          campaignId={campaignId}
          city={city}
          countryCode={countryCode}
          onSaved={() => {
            afterChange()
            boundaries.refetch()
          }}
          onClose={() => setAreaEditor({ open: false, mission: null })}
        />
      )}
    </LayoutDashboard>
  )
}

LetterboxMissionsV2.propTypes = {
  campaign: PropTypes.object.isRequired,
  city: PropTypes.object.isRequired,
  countryCode: PropTypes.string,
  weekScoped: PropTypes.bool,
  match: PropTypes.object.isRequired,
  showReduxModal: PropTypes.func.isRequired,
  hideReduxModal: PropTypes.func.isRequired,
}

LetterboxMissionsV2.defaultProps = {
  countryCode: null,
  weekScoped: false,
}

export default connect(
  null,
  { showReduxModal, hideReduxModal },
)(LetterboxMissionsV2)
